import { trpc } from "@/lib/trpc";
import { UNAUTHED_ERR_MSG } from '@shared/const';
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { httpBatchLink, TRPCClientError } from "@trpc/client";
import { hydrateRoot, createRoot } from "react-dom/client";
import superjson from "superjson";
import App from "./App";
import { captureAttribution } from "./hooks/useUtmParams";
import { getLoginUrl } from "./const";
import "./index.css";

// Record utm parameters and ad click ids on the landing page, before any
// client-side navigation can drop the query string.
captureAttribution();

const queryClient = new QueryClient();

const redirectToLoginIfUnauthorized = (error: unknown) => {
  if (!(error instanceof TRPCClientError)) return;
  if (typeof window === "undefined") return;

  const isUnauthorized = error.message === UNAUTHED_ERR_MSG;

  if (!isUnauthorized) return;

  window.location.href = getLoginUrl();
};

queryClient.getQueryCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.query.state.error;
    redirectToLoginIfUnauthorized(error);
    console.error("[API Query Error]", error);
  }
});

queryClient.getMutationCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.mutation.state.error;
    redirectToLoginIfUnauthorized(error);
    console.error("[API Mutation Error]", error);
  }
});

const trpcClient = trpc.createClient({
  links: [
    httpBatchLink({
      url: "/api/trpc",
      transformer: superjson,
      fetch(input, init) {
        return globalThis.fetch(input, {
          ...(init ?? {}),
          credentials: "include",
        });
      },
    }),
  ],
});

const rootEl = document.getElementById("root")!;
const appJsx = (
  <trpc.Provider client={trpcClient} queryClient={queryClient}>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </trpc.Provider>
);

// Determine whether to hydrate or create fresh.
// In development, the jsxLocPlugin adds data-loc attributes to client-rendered JSX
// that don't exist in SSR HTML, causing unavoidable hydration mismatches.
// We only hydrate in production where the SSR and client output match exactly.
const hasSSRContent = rootEl.innerHTML.trim().length > 0 && !rootEl.innerHTML.includes("<!--ssr-outlet-->");
const isDev = import.meta.env.DEV;

if (hasSSRContent && !isDev) {
  hydrateRoot(rootEl, appJsx, {
    onRecoverableError(error, errorInfo) {
      console.error("[Hydration recoverable error]", error, errorInfo.componentStack);
    },
  });
} else {
  // In dev mode or without SSR content, use createRoot for clean rendering
  // This avoids hydration mismatch errors from dev-only transforms (data-loc attributes)
  rootEl.innerHTML = "";
  createRoot(rootEl).render(appJsx);
}
